import { useEffect, useState } from "react";
import api, { rupiah, getErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

const NONE = "__none__";
const CAT_STYLE = {
  complete: "bg-green-100 text-green-700 border-green-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  na: "bg-slate-100 text-slate-600 border-slate-300",
  not_available: "bg-blue-100 text-blue-700 border-blue-300",
  intentional_blank: "bg-violet-100 text-violet-700 border-violet-300",
  review_required: "bg-red-100 text-red-700 border-red-300",
  exception: "bg-red-100 text-red-700 border-red-300",
};

export default function AuditStrategy({ id }) {
  const [d, setD] = useState(null);

  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const load = () => api.get(`/engagements/${id}/audit-strategy`).then((r) => setD(r.data));

  const setLocal = (si, fi, patch) => setD((prev) => {
    const nd = structuredClone(prev);
    Object.assign(nd.sections[si].fields[fi], patch);
    return nd;
  });

  const saveField = async (si, fi, patch = {}) => {
    const f = { ...d.sections[si].fields[fi], ...patch };
    const entry = {
      value: f.value, state: f.state, rationale: f.rationale, audit_impact: f.audit_impact,
      follow_up_action: f.follow_up_action, follow_up_owner: f.follow_up_owner,
      follow_up_due: f.follow_up_due,
      ...(Object.prototype.hasOwnProperty.call(patch,"reviewer_acknowledged") ? {reviewer_acknowledged:patch.reviewer_acknowledged} : {}),
    };
    try {
      const { data } = await api.put(`/engagements/${id}/audit-strategy/field/${f.key}`, entry);
      setD(data);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  if (!d) return <div className="text-sm text-muted-foreground p-4">Memuat strategi…</div>;
  const c = d.completeness;
  const s = d.summary;

  return (
    <div className="space-y-4">
      {/* SUMMARY PANEL */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="strategy-summary">
        {[
          ["Materialitas", rupiah(s.overall_materiality)],
          ["Risiko Signifikan", s.significant_risks],
          ["Risiko Kecurangan", s.fraud_risks],
          ["Andalan Pengendalian", s.control_reliance],
          ["Area Audit Kunci", s.key_audit_areas],
          ["EQR", s.eqr_required == null ? "Belum ditentukan" : s.eqr_required ? "Wajib" : "Tidak"],
          ["Status Strategi", s.strategy_status],
        ].map(([l, v]) => (
          <div key={l} className="bg-white border border-border rounded-sm p-2">
            <div className="text-sm font-head font-bold tabular truncate">{v}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {/* COMPLETENESS */}
      <div className="bg-white border border-border rounded-sm p-3 space-y-2" data-testid="strategy-completeness">
        <div className="flex items-center justify-between">
          <div className="font-head font-semibold text-sm">Kelengkapan Strategi</div>
          <div className={`text-lg font-head font-bold tabular ${c.score >= 100 ? "text-green-700" : c.score >= 60 ? "text-amber-700" : "text-red-700"}`}>{c.score}%</div>
        </div>
        <Progress value={c.score} className="h-2" />
        <div className="flex flex-wrap gap-2 text-[11px]">
          {[["Selesai", c.complete, "complete"], ["Pending", c.pending, "pending"], ["Kosong / Perlu Reviu", c.review_required, "review_required"], ["Tidak Berlaku", c.na, "na"], ["Belum Tersedia", c.not_available, "not_available"], ["Sengaja Dikosongkan", c.intentional_blank, "intentional_blank"], ["Eksepsi Reviewer", c.exceptions_count, "exception"]].map(([l, v, cat]) => (
            <span key={l} className={`px-2 py-0.5 border rounded-sm ${CAT_STYLE[cat]}`} data-testid={`comp-${cat}`}>{l}: <b>{v}</b></span>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground">Kosong tidak otomatis berarti "Tidak Berlaku". Setiap field material wajib punya status eksplisit; jika kosong tanpa status akan ditandai PERLU REVIU dan memblokir gerbang perencanaan.</div>
      </div>

      {/* REVIEWER EXCEPTIONS */}
      {d.exceptions.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-3" data-testid="strategy-exceptions">
          <div className="flex items-center gap-2 font-head font-semibold text-sm text-red-700 mb-2">
            <AlertTriangle size={15} /> Item Strategi Belum Lengkap / Kosong ({d.exceptions.length})
          </div>
          <div className="overflow-x-auto"><table className="w-full audit-table">
            <thead><tr><th>Field</th><th>Bagian</th><th>Status</th><th>Potensi Dampak Audit</th><th>Disiapkan Oleh</th><th>Tindakan Reviewer</th></tr></thead>
            <tbody>{d.exceptions.map((x) => (
              <tr key={x.field_key} data-testid={`exc-${x.field_key}`}>
                <td className="font-medium">{x.label}</td>
                <td className="text-xs text-muted-foreground">{x.section}</td>
                <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${CAT_STYLE[x.status_cat]}`}>{x.status}</span></td>
                <td className="text-xs">{x.audit_impact || <span className="text-red-600">{x.note}</span>}</td>
                <td className="text-xs">{x.prepared_by || "-"}</td>
                <td className="text-xs text-muted-foreground">{x.reviewer_action}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      {/* SECTIONS */}
      {d.sections.map((sec, si) => (
        <div key={sec.key} className="bg-white border border-border rounded-sm" data-testid={`strategy-section-${sec.key}`}>
          <div className="px-3 py-2 border-b border-border font-head font-semibold text-sm bg-muted/40">{sec.title}</div>
          <div className="divide-y divide-border/60">
            {sec.fields.map((f, fi) => (
              <FieldRow key={f.key} f={f} states={d.states}
                onLocal={(patch) => setLocal(si, fi, patch)}
                onSave={(patch) => saveField(si, fi, patch)} />
            ))}
          </div>
        </div>
      ))}

      {/* SIGN-OFF */}
      <Signoff id={id} d={d} setD={setD} />
    </div>
  );
}

function FieldRow({ f, states, onLocal, onSave }) {
  const showRationale = f.state === "Not Applicable" || f.state === "Intentionally Left Blank";
  const showFollowup = f.state === "Not Available";
  const showAck = f.state === "Intentionally Left Blank";
  const showImpact = ["Pending", "Not Applicable", "Not Available", "Intentionally Left Blank"].includes(f.state) || f.status_cat === "review_required";

  return (
    <div className="px-3 py-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`field-${f.key}`}>
      <div className="md:col-span-3">
        <div className="text-xs font-medium flex items-center gap-1">
          {f.label}{f.material && <span className="text-[9px] text-indigo-600 border border-indigo-200 rounded-sm px-1">material</span>}
        </div>
        <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 border rounded-sm ${CAT_STYLE[f.status_cat]}`} data-testid={`field-status-${f.key}`}>{f.status_label}</span>
      </div>
      <div className="md:col-span-5 space-y-1">
        {f.type === "textarea" ? (
          <Textarea rows={2} className="text-xs" value={f.value || ""} placeholder="Isi nilai…"
            onChange={(e) => onLocal({ value: e.target.value })} onBlur={() => onSave()} data-testid={`val-${f.key}`} />
        ) : f.type === "select" ? (
          <Select value={f.value || ""} onValueChange={(v) => onSave({ value: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`val-${f.key}`}><SelectValue placeholder="Pilih…" /></SelectTrigger>
            <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input type={f.type === "number" ? "number" : "text"} className="h-8 text-xs" value={f.value ?? ""} placeholder="Isi nilai…"
            onChange={(e) => onLocal({ value: f.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value })} onBlur={() => onSave()} data-testid={`val-${f.key}`} />
        )}
        {showImpact && (
          <Input className="h-7 text-[11px]" placeholder="Potensi dampak audit (opsional untuk reviewer)" value={f.audit_impact || ""}
            onChange={(e) => onLocal({ audit_impact: e.target.value })} onBlur={() => onSave()} data-testid={`impact-${f.key}`} />
        )}
        {showRationale && (
          <Input className="h-7 text-[11px]" placeholder="Rasional wajib (mis. alasan Tidak Berlaku / sengaja dikosongkan)" value={f.rationale || ""}
            onChange={(e) => onLocal({ rationale: e.target.value })} onBlur={() => onSave()} data-testid={`rationale-${f.key}`} />
        )}
        {showFollowup && (
          <div className="grid grid-cols-3 gap-1">
            <Input className="h-7 text-[11px]" placeholder="Tindak lanjut / PBC" value={f.follow_up_action || ""} onChange={(e) => onLocal({ follow_up_action: e.target.value })} onBlur={() => onSave()} data-testid={`fu-action-${f.key}`} />
            <Input className="h-7 text-[11px]" placeholder="Penanggung jawab" value={f.follow_up_owner || ""} onChange={(e) => onLocal({ follow_up_owner: e.target.value })} onBlur={() => onSave()} data-testid={`fu-owner-${f.key}`} />
            <Input className="h-7 text-[11px]" placeholder="Jatuh tempo" value={f.follow_up_due || ""} onChange={(e) => onLocal({ follow_up_due: e.target.value })} onBlur={() => onSave()} data-testid={`fu-due-${f.key}`} />
          </div>
        )}
        {showAck && (
          <label className="flex items-center gap-2 text-[11px]">
            <Checkbox checked={!!f.reviewer_acknowledged} onCheckedChange={(v) => onSave({ reviewer_acknowledged: !!v })} data-testid={`ack-${f.key}`} />
            Partner mengakui bahwa field ini sengaja dikosongkan {f.reviewer_acknowledged && <span>({f.reviewer_acknowledged_by_name} · {f.reviewer_acknowledged_at})</span>}
          </label>
        )}
      </div>
      <div className="md:col-span-4">
        <Select value={f.state || NONE} onValueChange={(v) => onSave({ state: v === NONE ? "" : v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`state-${f.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>— Belum dipilih (Perlu Reviu) —</SelectItem>
            {states.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
          </SelectContent>
        </Select>
        {f.status_note && <div className="text-[10px] text-muted-foreground mt-1">{f.status_note}</div>}
      </div>
    </div>
  );
}

function Signoff({ id, d, setD }) {
  const so=d.signoff;
  const save=async(patch)=>{
    try{const {data}=await api.put(`/engagements/${id}/audit-strategy/signoff`,patch);setD(data);toast.success("Sign-off strategi disimpan");}
    catch(e){toast.error(getErrorMessage(e));}
  };
  return <div className="bg-white border border-border rounded-sm p-3 space-y-2" data-testid="strategy-signoff">
    <div className="font-head font-semibold text-sm">16. Reviu & Sign-off</div>
    <p className="text-xs">Identitas dan waktu dicatat dari pengguna yang melakukan tindakan. Perubahan sumber memerlukan penilaian ulang.</p>
    {[["prepared_by","Persiapan","prepared_source_current"],["manager_review","Reviu Manajer","manager_source_current"],["partner_review","Persetujuan Partner","partner_source_current"]].map(([slot,label,current])=><div key={slot} className="flex items-center gap-2 text-xs">
      <span>{label}: {so[slot+'_user_id']?so[slot]:'Belum diatestasi'} · {so[slot+'_at']||''}{so[slot+'_user_id']&&so[current]===false?' · Sumber perlu dinilai ulang':''}</span>
      <Button size="sm" variant="outline" onClick={()=>save({[slot]:true})} data-testid={'so-'+slot}>{label}</Button>
      {so[slot+'_user_id']&&<button className="underline" onClick={()=>save({[slot]:false})}>Tarik {label}</button>}
    </div>)}
    <div className="text-xs">Kebutuhan EQR (Partner):<Select value={so.eqr_relevance||''} onValueChange={v=>save({eqr_relevance:v})}><SelectTrigger data-testid="so-eqr"><SelectValue placeholder="Belum ditentukan"/></SelectTrigger><SelectContent><SelectItem value="Required">Required</SelectItem><SelectItem value="Not Required">Not Required</SelectItem></SelectContent></Select></div>
    <p className="text-xs">Status: {so.status||'Draft'}</p>
    <Button size="sm" onClick={()=>save({status:'Finalized'})} data-testid="so-finalize">Finalisasi (Partner)</Button>
    <Button size="sm" variant="outline" onClick={()=>save({status:'Draft'})} data-testid="so-reopen">Buka kembali untuk penilaian ulang</Button>
    {!d.completeness.can_finalize&&<p className="text-xs text-red-700">Lengkapi item material, tindak lanjut, dan narasi kesimpulan sebelum persiapan/finalisasi.</p>}
  </div>;
}
